/**
 * @friskai/frisk-js Base Framework Adapter
 *
 * Default implementation of the FrameworkAdapter interface.
 * Use this as a base for custom adapters or when not using a specific framework.
 */

import { v4 as uuidv4 } from 'uuid'
import type { RegisterToolProperties } from '../core/tool-registry'
import type {
  RedactedObject,
  RedactedSerializedObject,
  RedactOption,
} from '../utils/redact'
import { redactObject } from '../utils/redact'
import type { FrameworkAdapter, ToolCallInfo } from './framework-adapter'

/**
 * Generic tool call type for base adapter
 */
export interface GenericToolCall {
  name: string
  args: Record<string, unknown>
  id?: string
}

/**
 * Base framework adapter implementation
 *
 * Provides a default implementation of the FrameworkAdapter interface
 * that works with generic tool call objects. Use this when not integrating
 * with a specific AI/LLM framework.
 *
 * @example
 * ```typescript
 * const adapter = new BaseFrameworkAdapter();
 *
 * const toolCallInfo = adapter.getToolCallInfo({
 *   name: 'send_email',
 *   args: { recipient: 'user@example.com' },
 * });
 * ```
 */
export class BaseFrameworkAdapter
  implements FrameworkAdapter<GenericToolCall, unknown>
{
  public readonly agentFramework = 'none'
  public readonly agentFrameworkVersion = null

  /**
   * Serialize tool arguments to JSON with optional redaction
   */
  serializeToolArgs(
    toolArgs: Record<string, unknown> | null | undefined,
    options?: { redact?: RedactOption },
  ): RedactedSerializedObject {
    if (toolArgs == null) {
      return { value: null, redactedPaths: [] }
    }

    const redacted = this.toolArgsToDict(toolArgs, options)
    return {
      value: JSON.stringify(redacted.value),
      redactedPaths: redacted.redactedPaths,
    }
  }

  /**
   * Serialize agent state to JSON with optional redaction
   */
  serializeAgentState(
    agentState: Record<string, unknown> | null | undefined,
    options?: { redact?: RedactOption },
  ): RedactedSerializedObject {
    if (agentState == null) {
      return { value: null, redactedPaths: [] }
    }

    const redacted = this.agentStateToDict(agentState, options)
    return {
      value: JSON.stringify(redacted.value),
      redactedPaths: redacted.redactedPaths,
    }
  }

  /**
   * Convert tool arguments to dictionary with optional redaction
   */
  toolArgsToDict(
    toolArgs: Record<string, unknown>,
    options?: { redact?: RedactOption },
  ): RedactedObject {
    return redactObject(toolArgs, options?.redact)
  }

  /**
   * Convert agent state to dictionary with optional redaction
   */
  agentStateToDict(
    agentState: Record<string, unknown>,
    options?: { redact?: RedactOption },
  ): RedactedObject {
    return redactObject(agentState, options?.redact)
  }

  /**
   * Extract tool call information from a generic tool call object
   */
  normalizeToolCall(toolCall: GenericToolCall): ToolCallInfo {
    return {
      id: toolCall.id ?? uuidv4(),
      name: toolCall.name,
      args: toolCall.args,
    }
  }

  /**
   * Extract user query from agent state
   */
  extractPrompt(agentState: Record<string, unknown>): string | null {
    const prompt = agentState['prompt']
    return typeof prompt === 'string' ? prompt : null
  }

  extractRegisterToolProperties(_tool: unknown): RegisterToolProperties | null {
    return null
  }
}
